'use client'

import {
  ChevronDown,
  ChevronRight,
  Search,
  Table,
  Link2,
  Key,
  ScanSearch,
  Database,
  Filter
} from 'lucide-react'
import React, { useState, useMemo } from 'react'

import {
  enrichedSchemaOverview,
  type EnrichedTableInfo,
  type ColumnInfo,
  type SchemaCategory
} from './enriched_vlink_schema'

interface TableViewProps {
  table: EnrichedTableInfo
  isExpanded: boolean
  onToggle: () => void
}

interface ColumnRowProps {
  column: ColumnInfo
  isKey: boolean
  isForeignKey: boolean
}

const ColumnRow: React.FC<ColumnRowProps> = ({
  column,
  isKey,
  isForeignKey
}) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-2 lg:gap-4 py-3 px-4 border-b border-gray-100 text-sm">
    {/* Column Name - spans full width on mobile */}
    <div className="flex items-center gap-2 col-span-1 md:col-span-2 lg:col-span-1 min-w-0">
      {isKey && <Key className="w-3 h-3 text-yellow-500 flex-shrink-0" />}
      {isForeignKey && (
        <Link2 className="w-3 h-3 text-blue-500 flex-shrink-0" />
      )}
      <span className="font-mono text-sm font-medium break-all">
        {column.name}
      </span>
    </div>

    {/* Data Type */}
    <div className="font-mono text-gray-600 text-sm truncate">
      <span className="inline-block lg:hidden text-xs text-gray-500 mr-2">
        Type:
      </span>
      {column.type}
    </div>

    {/* Nullable */}
    <div className="flex items-center lg:justify-center">
      <span className="inline-block lg:hidden text-xs text-gray-500 mr-2">
        Nullable:
      </span>
      <span
        className={`px-2 py-1 rounded text-xs inline-block ${
          column.nullable
            ? 'bg-orange-100 text-orange-700'
            : 'bg-green-100 text-green-700'
        }`}
      >
        {column.nullable ? 'NULL' : 'NOT NULL'}
      </span>
    </div>

    {/* Default */}
    <div className="font-mono text-gray-500 text-xs">
      <span className="inline-block lg:hidden text-xs text-gray-500 mr-2">
        Default:
      </span>
      <span className="break-all">{column.default || '-'}</span>
    </div>

    {/* Constraints */}
    <div className="text-xs min-w-0">
      <span className="inline-block lg:hidden text-xs text-gray-500 mr-2">
        Constraints:
      </span>
      <div className="flex flex-wrap gap-1">
        {column.constraints.map((constraint) => (
          <span
            className="bg-gray-100 text-gray-600 px-2 py-1 rounded whitespace-nowrap"
            key={constraint}
          >
            {constraint}
          </span>
        ))}
      </div>
    </div>

    {/* Key Type */}
    <div className="text-xs text-gray-500">
      <span className="inline-block lg:hidden text-xs text-gray-500 mr-2">
        Key Type:
      </span>
      {isKey && 'Primary Key'}
      {isForeignKey && 'Foreign Key'}
      {!isKey && !isForeignKey && '-'}
    </div>
  </div>
)

const TableView: React.FC<TableViewProps> = ({
  table,
  isExpanded,
  onToggle
}) => {
  const foreignKeyColumns = new Set(
    table.schema.foreignKeys.map((fk) => fk.column)
  )

  return (
    <div className="bg-white border border-gray-200 rounded-lg mb-4 overflow-hidden">
      <div
        aria-expanded={isExpanded}
        aria-label={`Toggle table details for ${table.name}`}
        className="p-4 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onToggle()
          }
        }}
        role="button"
        tabIndex={0}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isExpanded ? (
              <ChevronDown className="w-5 h-5 text-gray-600" />
            ) : (
              <ChevronRight className="w-5 h-5 text-gray-600" />
            )}
            <Table className="w-5 h-5 text-blue-600" />
            <div>
              <h4 className="font-mono font-semibold text-lg">{table.name}</h4>
              <p className="text-gray-600 text-sm mt-1">{table.description}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="text-sm font-semibold text-gray-700">
                {table.records}
              </div>
              <div className="text-xs text-gray-500">records</div>
            </div>
            <div className="text-right">
              <div className="text-sm font-semibold text-blue-600">
                {table.schema.columns.length}
              </div>
              <div className="text-xs text-gray-500">columns</div>
            </div>
            <div className="text-right">
              <div className="text-sm font-semibold text-green-600">
                {table.schema.foreignKeys.length}
              </div>
              <div className="text-xs text-gray-500">FK</div>
            </div>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="p-4 border-t border-gray-200">
          <div className="mb-4">
            <h5 className="text-lg font-semibold flex items-center gap-2">
              <Database className="w-5 h-5 text-gray-600" />
              Table Details
            </h5>
          </div>

          {/* Columns Table */}
          <div className="mb-6">
            <h6 className="font-semibold mb-3 flex items-center gap-2">
              <Table className="w-4 h-4" />
              Columns ({table.schema.columns.length})
            </h6>
            <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-2 lg:gap-4 px-4 py-3 bg-gray-50 font-semibold text-sm border-b">
                <div className="col-span-1 md:col-span-2 lg:col-span-1">
                  Column Name
                </div>
                <div className="hidden lg:block">Data Type</div>
                <div className="hidden lg:block lg:text-center">Nullable</div>
                <div className="hidden lg:block">Default</div>
                <div className="hidden lg:block">Constraints</div>
                <div className="hidden lg:block">Key Type</div>
              </div>
              <div className="max-h-96 overflow-y-auto overflow-x-hidden">
                {table.schema.columns.map((column) => (
                  <ColumnRow
                    column={column}
                    isForeignKey={foreignKeyColumns.has(column.name)}
                    isKey={table.schema.primaryKeys.includes(column.name)}
                    key={column.name}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Foreign Keys */}
          {table.schema.foreignKeys.length > 0 && (
            <div className="mb-6">
              <h6 className="font-semibold mb-3 flex items-center gap-2">
                <Link2 className="w-4 h-4" />
                Foreign Keys ({table.schema.foreignKeys.length})
              </h6>
              <div className="space-y-2">
                {table.schema.foreignKeys.map((fk, index) => (
                  <div
                    className="bg-blue-50 p-3 rounded border-l-4 border-blue-400"
                    key={index}
                  >
                    <div className="font-mono text-sm">
                      <span className="text-blue-700 font-semibold">
                        {fk.column}
                      </span>
                      <span className="mx-2">→</span>
                      <span className="text-gray-700">{fk.references}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Indexes */}
          {table.schema.indexes.length > 0 && (
            <div className="mb-6">
              <h6 className="font-semibold mb-3 flex items-center gap-2">
                <ScanSearch className="w-4 h-4" />
                Indexes ({table.schema.indexes.length})
              </h6>
              <div className="space-y-2">
                {table.schema.indexes.map((index, idx) => (
                  <div className="bg-gray-50 p-3 rounded border" key={idx}>
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-sm font-semibold">
                        {index.name}
                      </span>
                      {index.type && (
                        <span className="bg-gray-200 text-gray-700 px-2 py-1 rounded text-xs">
                          {index.type}
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      Columns: {index.columns.join(', ')}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

interface CategorySectionProps {
  categoryKey: string
  category: SchemaCategory
  expandedTables: Set<string>
  onTableToggle: (tableId: string) => void
  searchTerm: string
}

const CategorySection: React.FC<CategorySectionProps> = ({
  categoryKey,
  category,
  expandedTables,
  onTableToggle,
  searchTerm
}) => {
  const [isExpanded, setIsExpanded] = useState(true)
  const Icon = category.icon

  const filteredTables = category.tables.filter(
    (table) =>
      table.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      table.description.toLowerCase().includes(searchTerm.toLowerCase())
  )

  if (filteredTables.length === 0) return null

  return (
    <div className="mb-8">
      <div
        className="flex items-center gap-3 p-4 bg-white rounded-lg shadow-sm border cursor-pointer hover:shadow-md transition-shadow mb-4"
        onClick={() => setIsExpanded(!isExpanded)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setIsExpanded(!isExpanded)
          }
        }}
        role="button"
        tabIndex={0}
      >
        {isExpanded ? (
          <ChevronDown className="w-6 h-6 text-gray-600" />
        ) : (
          <ChevronRight className="w-6 h-6 text-gray-600" />
        )}
        <div className={`p-2 rounded-lg ${category.color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <h3 className="text-xl font-bold capitalize">{categoryKey}</h3>
          <p className="text-gray-600">{category.description}</p>
          <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
            <span>{filteredTables.length} tables</span>
            <span>•</span>
            <span>{category.relationships.length} relationships</span>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="space-y-4 ml-4">
          {filteredTables.map((table) => {
            const tableId = `${categoryKey}-${table.name}`

            return (
              <TableView
                isExpanded={expandedTables.has(tableId)}
                key={tableId}
                onToggle={() => onTableToggle(tableId)}
                table={table}
              />
            )
          })}

          {/* Relationships */}
          {category.relationships.length > 0 && (
            <div className="bg-gray-50 p-4 rounded-lg border">
              <h4 className="font-semibold mb-2 flex items-center gap-2">
                <Link2 className="w-4 h-4" />
                Key Relationships
              </h4>
              <ul className="space-y-1 text-sm text-gray-600">
                {category.relationships.map((relationship, index) => (
                  <li className="font-mono" key={index}>
                    {relationship}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

const VlinkSchemaOverview: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [expandedTables, setExpandedTables] = useState<Set<string>>(new Set())
  const [selectedCategory, setSelectedCategory] = useState<string>('all')

  const handleTableToggle = (tableId: string) => {
    const newExpanded = new Set(expandedTables)

    if (newExpanded.has(tableId)) {
      newExpanded.delete(tableId)
    } else {
      newExpanded.add(tableId)
    }
    setExpandedTables(newExpanded)
  }

  const categories = Object.keys(enrichedSchemaOverview)

  const filteredCategories = useMemo(() => {
    if (selectedCategory === 'all') {
      return Object.entries(enrichedSchemaOverview)
    }

    return Object.entries(enrichedSchemaOverview).filter(
      ([key]) => key === selectedCategory
    )
  }, [selectedCategory])

  const totalTables = Object.values(enrichedSchemaOverview).reduce(
    (sum, category) => sum + category.tables.length,
    0
  )

  const totalColumns = Object.values(enrichedSchemaOverview).reduce(
    (sum, category) =>
      sum +
      category.tables.reduce(
        (tableSum, table) => tableSum + table.schema.columns.length,
        0
      ),
    0
  )

  const totalForeignKeys = Object.values(enrichedSchemaOverview).reduce(
    (sum, category) =>
      sum +
      category.tables.reduce(
        (tableSum, table) => tableSum + table.schema.foreignKeys.length,
        0
      ),
    0
  )

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Vlink Database Schema
          </h1>
          <p className="text-gray-600 text-lg">
            Complete overview of the Vlink database structure with detailed
            schema information for all {totalTables} tables.
          </p>

          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
            <div className="bg-white p-4 rounded-lg shadow-sm border">
              <div className="text-2xl font-bold text-blue-600">
                {totalTables}
              </div>
              <div className="text-gray-600">Tables</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm border">
              <div className="text-2xl font-bold text-green-600">
                {totalColumns}
              </div>
              <div className="text-gray-600">Columns</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm border">
              <div className="text-2xl font-bold text-orange-600">
                {totalForeignKeys}
              </div>
              <div className="text-gray-600">Foreign Keys</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm border">
              <div className="text-2xl font-bold text-purple-600">
                {categories.length}
              </div>
              <div className="text-gray-600">Categories</div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg shadow-sm border mb-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search tables, columns, or descriptions..."
                type="text"
                value={searchTerm}
              />
            </div>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <select
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
                onChange={(e) => setSelectedCategory(e.target.value)}
                value={selectedCategory}
              >
                <option value="all">All Categories</option>
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category.charAt(0).toUpperCase() + category.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Schema Categories */}
        <div>
          {filteredCategories.map(([categoryKey, category]) => (
            <CategorySection
              category={category}
              categoryKey={categoryKey}
              expandedTables={expandedTables}
              key={categoryKey}
              onTableToggle={handleTableToggle}
              searchTerm={searchTerm}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

export default VlinkSchemaOverview
